import React, { useState } from 'react';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core';
import {
  Logo10Icon,
  Logo11Icon,
  Logo12Icon,
  Logo1Icon,
  Logo2Icon,
  Logo3Icon,
  Logo4Icon,
  Logo5Icon,
  Logo6Icon,
  Logo7Icon,
  Logo8Icon,
  Logo9Icon,
  LogoOnHoverIcon,
  MainLogoIcon,
} from '../../svgs/about-us';
import Link from 'next/link';

// Define styles for the component
const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    root: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      margin: '0px -15px',
      [theme.breakpoints.up('sm')]: {
        margin: 0,
      },
    },
    square: {
      position: 'relative',
      width: '100%',
      maxWidth: 347,
      height: 347,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: 50,
      [theme.breakpoints.up('sm')]: {
        height: 500,
        maxWidth: 500,
      },
      [theme.breakpoints.up('lg')]: {
        marginTop: 0,
      },
    },
    centerText: {
      position: 'absolute',
      zIndex: 10,
      transform: 'scale(0.75)',
      [theme.breakpoints.up('sm')]: {
        transform: 'scale(1)',
      },
    },
    word: {
      position: 'absolute',
      cursor: 'pointer',
      transform: 'scale(0.67)',
      [theme.breakpoints.up('sm')]: {
        transform: 'scale(1)',
      },
      '&:hover': {
        '& svg': {
          fill: theme.palette.primary.main, // Change fill color on hover
          '& path': {
            fill: 'white', // Change path fill color to white
          },
          '& path:nth-child(1)': {
            fill: theme.palette.primary.main, // Specific hover styling for the first path
          },
          '& g': {
            stroke: 'white !important',
            '& path': {
              fill: 'white !important', // Force white fill for paths in group
            },
          },
        },
      },
    },
    wordIcon: {
      position: 'absolute',
      cursor: 'pointer',
      transform: 'scale(0.67)',
      [theme.breakpoints.up('sm')]: {
        transform: 'scale(1)',
      },
    },
    // Corner styles
    cornerTopLeft: { top: 0, left: 0 },
    cornerTopRight: { top: 0, right: 0 },
    cornerBottomLeft: { bottom: 0, left: 0 },
    cornerBottomRight: { bottom: 0, right: 0 },
    // Top side words (two)
    sideTop1: {
      top: 0,
      left: '23%',
      [theme.breakpoints.up('sm')]: {
        left: '26%',
      },
    },
    sideTop2: {
      top: 0,
      left: '46%',
      [theme.breakpoints.up('sm')]: {
        left: '52%',
      },
    },
    // Right side words (two)
    sideRight1: {
      top: '22.5%',
      right: 0,
      [theme.breakpoints.up('sm')]: {
        top: '26%',
      },
    },
    sideRight2: {
      top: '45%',
      right: 0,
      [theme.breakpoints.up('sm')]: {
        top: '52%',
      },
    },
    // Bottom side words (two)
    sideBottom1: {
      bottom: 0,
      left: '23%',
      [theme.breakpoints.up('sm')]: {
        left: '26%',
      },
    },
    sideBottom2: {
      bottom: 0,
      left: '46%',
      [theme.breakpoints.up('sm')]: {
        left: '52%',
      },
    },
    // Left side words (two)
    sideLeft1: {
      top: '22.5%',
      left: 0,
      [theme.breakpoints.up('sm')]: {
        top: '26%',
      },
    },
    sideLeft2: {
      top: '45%',
      left: 0,
      [theme.breakpoints.up('sm')]: {
        top: '52%',
      },
    },
  });
});

const LogoBlock = () => {
  const classes = useStyles();
  const [isHovered, setIsHovered] = useState(false); // State to track hover status

  return (
    <div className={classes.root}>
      <div className={classes.square}>
        {/* Center logo */}
        <div className={classes.centerText}>
          <MainLogoIcon />
        </div>
        {/* Corner logos */}
        <div className={`${classes.word} ${classes.cornerTopLeft}`}>
          <Link href={'/expertise/engineering'}>
            <Logo12Icon />
          </Link>
        </div>
        <div className={`${classes.word} ${classes.cornerBottomRight}`}>
          <Link href={'/expertise/consulting-business-services'}>
            <Logo3Icon />
          </Link>
        </div>
        <div className={`${classes.word} ${classes.cornerTopRight}`}>
          <Link href={'/expertise/construction'}>
            <Logo1Icon />
          </Link>
        </div>
        <div className={`${classes.word} ${classes.cornerBottomLeft}`}>
          <Link href={'/expertise/oil-gas'}>
            <Logo6Icon />
          </Link>
        </div>
        {/* Top side logos */}
        <div className={`${classes.word} ${classes.sideTop1}`}>
          <Link href={'/expertise/wireless'}>
            <Logo10Icon />
          </Link>
        </div>
        <div className={`${classes.word} ${classes.sideTop2}`}>
          <Link href={'/expertise/energy'}>
            <Logo9Icon />
          </Link>
        </div>
        {/* Right side logos */}
        <div className={`${classes.word} ${classes.sideRight1}`}>
          <Link href={'/expertise/technology-deployment/noc'}>
            <Logo11Icon />
          </Link>
        </div>
        <div className={`${classes.word} ${classes.sideRight2}`}>
          <Link href={'/expertise/water-utility-services'}>
            <Logo2Icon />
          </Link>
        </div>
        {/* Bottom side logos */}
        <div
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className={`${isHovered ? classes.wordIcon : classes.word} ${classes.sideBottom1}`}
        >
          <Link href={'/expertise/wireline'}>{isHovered ? <LogoOnHoverIcon /> : <Logo5Icon />}</Link>
        </div>
        <div className={`${classes.word} ${classes.sideBottom2}`}>
          <Link href={'/expertise/fulfillment-services'}>
            <Logo4Icon />
          </Link>
        </div>
        {/* Left side logos */}
        <div className={`${classes.word} ${classes.sideLeft1}`}>
          <Link href={'/expertise/data-centers-capabilities'}>
            <Logo8Icon />
          </Link>
        </div>
        <div className={`${classes.word} ${classes.sideLeft2}`}>
          <Link href={'/expertise/power-delivery'}>
            <Logo7Icon />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LogoBlock;
